import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { AuthContext } from '@/context/auth-context'
import { ApiError } from '@/util/errors/api-error'
import { getOcrQuota } from '../fetch/get-ocr-quota'
import type { OcrQuotaSummary } from '../schemas'

type OcrQuotaSummaryState = {
  summary: OcrQuotaSummary | null
  /** When the request that produced `summary` started, so callers can tell whether it predates a change they saw. */
  requestedAt: number | null
  loading: boolean
  error: string | null
  refresh: () => Promise<void>
}

export function useOcrQuotaSummary({ enabled = true }: { enabled?: boolean } = {}): OcrQuotaSummaryState {
  const { user, userAccountData } = useContext(AuthContext)
  const accountKey = `${user?.uid ?? ''}:${userAccountData?.orgData?.id ?? ''}`
  const [result, setResult] = useState<{ accountKey: string; summary: OcrQuotaSummary | null; requestedAt: number | null }>({
    accountKey,
    summary: null,
    requestedAt: null,
  })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const requestIdRef = useRef(0)
  const mountedRef = useRef(true)

  const refresh = useCallback(async () => {
    if (!enabled) return
    const requestId = requestIdRef.current + 1
    requestIdRef.current = requestId
    const requestedAt = Date.now()
    setLoading(true)
    setError(null)

    try {
      const nextSummary = await getOcrQuota()
      if (!mountedRef.current || requestIdRef.current !== requestId) return
      setResult({ accountKey, summary: nextSummary, requestedAt })
    } catch (requestError) {
      if (!mountedRef.current || requestIdRef.current !== requestId) return

      // An older backend does not expose the quota summary route. Treat that
      // rollout state as an absent optional surface instead of a user-facing error.
      if (requestError instanceof ApiError && requestError.status === 404) {
        setResult({ accountKey, summary: null, requestedAt })
        return
      }

      setError('OCR usage is temporarily unavailable.')
    } finally {
      if (mountedRef.current && requestIdRef.current === requestId) {
        setLoading(false)
      }
    }
  }, [accountKey, enabled])

  useEffect(() => {
    mountedRef.current = true
    setResult({ accountKey, summary: null, requestedAt: null })
    void refresh()

    return () => {
      mountedRef.current = false
      requestIdRef.current += 1
    }
  }, [accountKey, refresh])

  const currentAccount = result.accountKey === accountKey
  return {
    summary: currentAccount ? result.summary : null,
    requestedAt: currentAccount ? result.requestedAt : null,
    loading: !currentAccount || loading,
    error: currentAccount ? error : null,
    refresh,
  }
}
